import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import type { Theme } from '../../shared/types';
import { getInitialTheme, persistTheme, watchSystem } from './theme';

export type Days = 7 | 30 | 90;
export const WINDOWS: Days[] = [7, 30, 90];
const KEY_WIN = 'canary.win';

const ThemeCtx = createContext<{ theme: Theme; setTheme: (t: Theme) => void } | null>(null);
const WindowCtx = createContext<{ days: Days; setDays: (d: Days) => void } | null>(null);

export function AppProviders({ children }: { children: ComponentChildren }) {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());
  const [days, setDaysState] = useState<Days>(() => {
    const v = Number(localStorage.getItem(KEY_WIN));
    return v === 30 || v === 90 ? v : 7;
  });

  useEffect(() => {
    persistTheme(theme);
    if (theme !== 'system') return;
    return watchSystem(() => persistTheme('system'));
  }, [theme]);

  const setDays = useCallback((d: Days) => {
    setDaysState(d);
    localStorage.setItem(KEY_WIN, String(d));
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme }}>
      <WindowCtx.Provider value={{ days, setDays }}>{children}</WindowCtx.Provider>
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  const t = useContext(ThemeCtx);
  if (!t) throw new Error('useTheme outside provider');
  return t;
}
export function useTimeWindow() {
  const w = useContext(WindowCtx);
  if (!w) throw new Error('useTimeWindow outside provider');
  return w;
}
